import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../../../@types/redux';
import { addkg, addea, formatDateTime, upsertArrayElement } from '../../../../services/appFunctionsService';
import { dataSetAllPackRelatedData } from '../../../../store/masterData/Functions';
import { generalShowErrorSnackbar, generalShowSuccessSnackbar } from '../../../../store/general/Functions';
import PackRelatedDataHttpService from '../../../../services/http/masterData/packRelatedDataHttpService';
import PackHttpService from '../../../../services/http/masterData/packHttpService';
import PackmanDialog from '../../../../components/dialog/PackmanDialog';
import PackForm from './form/PackForm';
import { dataSetPacks } from '../../../../store/masterData/Actions';
import PackSummary from './PackSummary';
import { ConfirmationDialog, CustomTable, ProjectDialog, Screen } from '@zz2/zz2-ui';
import { IApiError } from '../../../../@types/other';
import { setPackMasterDataIndexedDB, syncMasterData } from '../../../../services/masterDataSyncService';
import { ICommodity } from '../../../../@types/model/masterData/commodity/commodity';
import { useLocation } from 'react-router-dom';
import { IPackFormValues } from '../../../../@types/model/masterData/pack/packFormValues';
import { IPack } from '../../../../@types/model/masterData/pack/pack';
import PackModelHelper from '../../../../@types/model/masterData/pack/packModelHelper';
import UserHelper from '../../../../services/helper/userHelper';
import { RightGrantsEnum } from '../../../../@types/enum/rightGrantsEnum';

interface IPackScreenProps {
    selectedCommodity : ICommodity | null;
}

const PackScreen = (props : IPackScreenProps) : React.ReactElement => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const currentUser = useAppSelector(x => x.auth.session?.user ?? null);
    const packs = useAppSelector(x => x.masterData.packs);
    const commodities = useAppSelector(x => x.masterData.commodities);
    const packCategories = useAppSelector(x => x.masterData.packCategories);
    const sizes = useAppSelector(x => x.masterData.sizes);
    const unitOfMeasures = useAppSelector(x => x.masterData.unitOfMeasures);
    const materials = useAppSelector(x => x.masterData.materials);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [selectedPack, setSelectedPack] = useState<IPack | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
    const [deletingPack, setDeletingPack] = useState<IPack | null>(null);

    const [isSummaryOpen, setIsSummaryOpen] = useState<boolean>(false);

    /*================================================================================================================
     *                                                  Queries
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        fetchData();
    }, []);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    const fetchData = async () => {
        const isIndexedDBAvailable = !!self.indexedDB;
        if (isIndexedDBAvailable) {
            await syncMasterData(false);
        } else {
            setIsLoading(true);
            try {
                const res = await PackRelatedDataHttpService.getAllPackRelatedData();
                dataSetAllPackRelatedData(res.data);
            } catch (error : unknown) {
                const apiError = error as IApiError | undefined;
                generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred retrieving pack related data.');
            } finally {
                setIsLoading(false);
            }
        }
    };

    const onSubmit = async (values : IPackFormValues) => {
        setIsLoading(true);

        try {
            const upsert = PackModelHelper.createUpsert(values);
            const res = await PackHttpService.addOrUpdatePack(upsert);
            const updatedList = upsertArrayElement(packs, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetPacks(updatedList));
            await setPackMasterDataIndexedDB(updatedList);

            generalShowSuccessSnackbar(selectedPack ? 'Pack updated successfully.' : 'Pack added successfully.');
            onModalDismiss();
        } catch (error : unknown) {
            const apiError = error as IApiError | undefined;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred updating pack data.');
        } finally {
            setIsLoading(false);
        }
    };

    const removePack = async () => {
        if (!deletingPack) return;
        const updatedPack = { ...deletingPack, isActive: false };
        setIsLoading(true);
        try {
            const res = await PackHttpService.deletePack(updatedPack.id);
            const updatedList = upsertArrayElement(packs, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetPacks(updatedList));
            await setPackMasterDataIndexedDB(updatedList);
            generalShowSuccessSnackbar('Pack deleted successfully.');
        } catch (error : unknown) {
            const apiError = error as IApiError | undefined;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred deleting pack.');
        } finally {
            closeDeletePopup();
            setIsLoading(false);
        }
    };

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const openModal = () => {
        setIsDialogOpen(true);
    };

    const onModalDismiss = () => {
        setIsDialogOpen(false);
        setSelectedPack(null);
    };

    const edit = (selectedRow : IPack) => {
        setSelectedPack(selectedRow);
        openModal();
    };

    const onDelete = (selectedRow : IPack) : void => {
        setDeletingPack(selectedRow);
    };

    const closeDeletePopup = () : void => {
        setDeletingPack(null);
    };

    const addButtonTooltip = () : string => {
        let message = 'Add';

        if (!userHasRights.addRight) {
            message = UserHelper.getRelatedRightToolTipMessage('Pack', RightGrantsEnum.Add);
        }

        return message;
    };

    const deleteButtonTooltip = (row : IPack) : string => {
        let message = 'Delete';

        if (!userHasRights.deleteRight) {
            message = UserHelper.getRelatedRightToolTipMessage('Pack', RightGrantsEnum.Delete);
        } else if (!row.isActive) {
            message = 'Already Inactive.';
        }

        return message;
    };

    const showSummaryDialog = (pack : IPack) => {
        setSelectedPack(pack);
        setIsSummaryOpen(true);
    };

    const closeSummaryDialog = () => {
        setSelectedPack(null);
        setIsSummaryOpen(false);
    };

    const formatSizes = (sizeIds : Array<number>) : string => {
        return sizes
            .filter(x => sizeIds.some(y => y === x.id))
            .map(x => x.code).toString().replace(/,/g, ', ');
    };

    const formatCategory = (catId : number) : string => {
        const category = packCategories.find(x => x.id === catId);
        return category ? category.category : '';
    };

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const initialValues = useMemo<IPackFormValues>(() => {
        return PackModelHelper.createFormValues(
            selectedPack,
            packs,
            commodities,
            packCategories,
            sizes,
            unitOfMeasures,
            materials,
            props.selectedCommodity
        );
    }, [
        selectedPack,
        isDialogOpen,
        packs,
        packCategories,
        sizes,
        unitOfMeasures,
        materials,
        props.selectedCommodity
    ]);

    const userHasRights = useMemo(() => {
        return UserHelper.userRelatedRights(location.pathname, currentUser?.rights);
    }, [currentUser, location]);

    const rows = useMemo<Array<IPack>>(() => {
        if (!props.selectedCommodity) return [];
        return packs.filter(x => x.commodityId === props.selectedCommodity?.id);
    }, [props.selectedCommodity, packs]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <Screen isLoading={isLoading} useAnimatedLoader>
            <Card className='fdc hfill'>
                <CustomTable<IPack>
                    
                    enableAdding
                    // addTooltipRightValidation={!userHasRights.addRight}
                    disableAddButton={!userHasRights.addRight || isLoading}
                    addTooltip={addButtonTooltip()}
                    addFunction={openModal}
                    // addTooltipOnClick={() : void => requestAccess('Add')}
                
                    enableEditing
                    // editTooltipRightValidation={!userHasRights.editRight}
                    disableEditButton={!userHasRights.editRight || isLoading}
                    editTooltip={!userHasRights.editRight ? UserHelper.getRelatedRightToolTipMessage('Pack', RightGrantsEnum.Edit) : 'Edit'}
                    editFunction={edit}
                    // editTooltipOnClick={() : void => requestAccess('Edit')}

                    enableDeleting
                    // deleteTooltipRightValidation={!userHasRights.deleteRight}
                    disableDeleteButton={(row : IPack) : boolean => !userHasRights.deleteRight || !row.isActive || isLoading}
                    deleteTooltip={deleteButtonTooltip}
                    deleteFunction={onDelete}
                    // deleteTooltipOnClick={() : void => requestAccess('Delete')}

                    enableDetails={true}
                    detailIcon={'info'}
                    detailFunction={showSummaryDialog}
                
                    enableSorting
                    enableFiltering
                    enablePagination
                    fitWidthToPage
                    columns={[
                        { title: 'Code', field: 'code', enableFiltering: true, enableSorting: true },
                        { title: 'Description', field: 'description', enableFiltering: true, enableSorting: true },
                        { title: 'Category', field: 'categoryId', formatFunction: formatCategory, enableFiltering: true, enableSorting: true },
                        { title: 'Sizes', field: 'sizeIds', formatFunction: formatSizes, width: 200, enableFiltering: true, enableSorting: true },
                        { title: 'Net Weight', field: 'netWeight', formatFunction: addkg, enableFiltering: true, enableSorting: true },
                        { title: 'Gross Weight', field: 'grossWeight', formatFunction: addkg, enableFiltering: true, enableSorting: true },
                        { title: 'Units', field: 'units', formatFunction: addea, enableFiltering: true, enableSorting: true },
                        { title: 'Created By', field: 'createdByName', enableFiltering: true, enableSorting: true },
                        { title: 'Created On', field: 'createdOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                        { title: 'Updated By', field: 'updatedByName', enableFiltering: true, enableSorting: true },
                        { title: 'Updated On', field: 'updatedOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                        { title: 'Active?', field: 'isActive', type: 'boolean', enableFiltering: true, enableSorting: true },
                    ]}
                    rows={rows}
                    initialSortOrder={[{ columnName: 'id_Id', direction: 'asc' }]}
                    pageSizes={[50, 150, 250, 500, 1000]}
                    pageHeight={190}
                    isActive={row => row.isActive}
                />
            </Card>
            <PackmanDialog
                title={'Pack Summary'}
                isInfo={true}
                isOpen={isSummaryOpen}
                onClose={closeSummaryDialog}
                fullWidth
                maxWidth={'lg'}
            >
                {selectedPack &&
                    <div className={'p20'}>
                        <PackSummary selectedPack={selectedPack} />
                    </div>
                }
            </PackmanDialog>
            {isDialogOpen &&
                <ProjectDialog
                    title={selectedPack ? 'Edit Pack' : 'Add Pack'}
                    isLoadingCircular={isLoading}
                    isOpen={isDialogOpen}
                    onClose={onModalDismiss}
                    fullScreen
                    fullWidth
                >
                    <PackForm
                        isLoading={isLoading}
                        initialValues={initialValues}
                        onSubmit={onSubmit}
                        onClose={onModalDismiss}
                    />
                </ProjectDialog>
            }
            <ConfirmationDialog
                title={'Inactivate Pack'}
                open={!!deletingPack}
                description={'Are you sure you would like to set the following item to inactive?'}
                onAccept={removePack}
                onClose={closeDeletePopup}
                dialogType={'red'}
                isLoading={isLoading}
            />
        </Screen>
    );
};

export default PackScreen;
